import HBLoading from "@/components/HBLoading";
import { sortType } from "@/components/UserInfo/components/BoardListPanel";
import UserInfo02 from "@/components/UserInfo02";
import { API_CACHEKEY } from "@/constants/cacheKey";
import useAppStore from "@/context/store";
import {
  getCollectionBoards,
  getLikedTopics,
  getMuseBoards,
  getMUserPins,
  getPinsByTag,
  getUser,
  getUserBoards,
  getUserFavoriteBoards,
  getUserFavoritePins,
  getUserPins,
} from "@/services";
import { mobileHost } from "@/utils/host";
import imageUrl from "@/utils/imageUrl";
import { boardFilter, pinListFilter } from "@/utils/server/dataFilter";
import { isLogined } from "@/utils/server/isLogined";
import isMobile from "@/utils/server/isMobile";
import {
  serverSideNotFound,
  serverSideRedirect,
} from "@/utils/server/serverSide";
import useRequest from "@/utils/useReq";
import Head from "next/head";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";

export async function getServerSideProps({ req, params, query, res: nextRes }) {
  const { filterType, collection_id: collectionId, sort, type, time } = query;
  const { urlname, openTab, tagName } = params;

  if (isMobile(req.headers["user-agent"], { excludePad: true })) {
    return serverSideRedirect(nextRes, {
      destination: `https://${mobileHost}/${req.url.replace(/^\/user/, "")}`,
      permanent: true,
    });
  }

  if (isLogined(req) || req.url.includes(".json")) {
    return {
      props: {},
    };
  }

  let encodedUrlname = encodeURIComponent(urlname);
  const { err, user } = await getUser({ urlname: encodedUrlname });

  if (String(err) === "404") {
    return serverSideNotFound(nextRes);
  }

  const props = { serversideUser: user ?? null };
  const PAGE_SIZE = 30;

  if (openTab === "creations") {
    const res = await getMUserPins({
      username: encodedUrlname,
      limit: PAGE_SIZE,
    });

    props.serversideMusePins = pinListFilter({ ...res, extra: ["user"] })
      .filter((e) => !!e)
      .map(({ pin, ...rest }) => ({
        ...rest,
        ...pin,
      }));
  } else if (openTab === "pins") {
    let params = {};
    const [date_since, date_until] = time?.split("-") || [];
    if (type) {
      params = {
        per_page: PAGE_SIZE,
        page: 1,
        material: type === "material",
        ...(type !== "material" ? { category: type } : null),
      };
    } else {
      params = {
        date_since: Number(date_since) || undefined,
        date_until: Number(date_until) || undefined,
        limit: PAGE_SIZE,
      };
    }
    const res = await getUserPins({
      username: encodedUrlname,
      ...params,
    });

    props.serversidePins = pinListFilter({ ...res, extra: ["user"] });
  } else if (openTab === "likes") {
    if (tagName === "boards") {
      const {
        err,
        user: { likes_boards: boards },
      } = await getUserFavoriteBoards({
        username: encodedUrlname,
        limit: PAGE_SIZE,
      });
      if (!err) {
        props.serversideLikesBoards = boards;
      }
    } else if (tagName === "topics") {
      const {
        err,
        user: { likes_topics: topics },
      } = await getLikedTopics({
        username: encodedUrlname,
        limit: PAGE_SIZE,
      });

      if (!err) {
        props.serversideLikesBoards = topics.map((topic) => ({
          title: topic?.detail?.banner?.materials?.[0]?.title,
          urlname: topic?.urlname,
          url: `/topics/${topic?.urlname}`,
          file: topic?.preview?.pins?.[0]?.file,
          keyword_id: topic?.detail?.id,
          seq: topic?.seq,
          following: topic?.following,
          liked: topic?.liked,
        }));
      }
    } else {
      if (tagName) {
        return serverSideNotFound(nextRes);
      }
      const res = await getUserFavoritePins({
        username: encodedUrlname,
        limit: PAGE_SIZE,
      });
      props.serversideLikesPins = res.pins;
    }
  } else if (openTab === "tags") {
    if (tagName) {
      const res = await getPinsByTag({
        urlname: encodedUrlname,
        tag_name: encodeURIComponent(tagName),
        limit: PAGE_SIZE,
      });

      props.serversideTagPins = res.pins || null;
    }
  } else {
    const api =
      filterType === "creation"
        ? getMuseBoards
        : collectionId
        ? getCollectionBoards
        : getUserBoards;
    let orderParam = {};

    switch (Number(sort)) {
      case sortType.TimeDown:
        orderParam = {
          order_by: "board_id",
        };
        break;
      case sortType.TimeUp:
        orderParam = {
          order_by: "board_id",
          since: 0,
        };
        break;
      case sortType.RecentUpdates:
        orderParam = {
          order_by: "updated_at",
        };
        break;
    }
    const params = {
      collection_id: collectionId,
      username: user.urlname,
      urlname: user.urlname,
      limit: PAGE_SIZE,
      ...orderParam,
    };
    const res = await api(params);

    props.serversideBoards =
      boardFilter(res).filter(
        (board) => board.is_private !== 2 || board.pins.length > 0
      ) || [];
  }

  return { props };
}

/**
 *
 * @returns {JSX.Element}
 * @constructor
 */
export default function UserInfoWithTitle({
  serversideUser,
  serversideBoards,
  serversidePins,
  serversideMusePins,
  serversideLikesPins,
  serversideLikesBoards,
  serversideLikesTopics,
  serversideTags,
  serversideTagPins,
}) {
  const router = useRouter();
  const { urlname, openTab, tagName, ...restQuery } = router.query;
  const [user, setUser] = useState(serversideUser || null);
  const { user: me, userReady } = useAppStore(({ user, userReady }) => ({
    user,
    userReady,
  }));

  const { runAsync: getUserCache } = useRequest(getUser, {
    cacheKey: `${API_CACHEKEY.USER}_${urlname}`,
    staleTime: 3000,
    manual: true,
  });

  const init = useCallback(async () => {
    if (urlname === me?.urlname) {
      setUser(me);
      return;
    }
    const { err, user } = await getUserCache({ urlname, ignoreMessage: true });
    if (err) {
      router.push("/404", `/user/${urlname}`);
      return;
    }
    setUser(user);
  }, [me, router, urlname, getUserCache]);

  useEffect(() => {
    if (urlname && userReady && user?.urlname !== urlname) {
      init();
    }
  }, [init, urlname, user?.urlname, userReady]);

  // 进入用户页面时清空 pinStack，确保弹窗返回逻辑正确
  useEffect(() => {
    const { clearPinStack, pinStack } = useAppStore.getState();
    if (pinStack && pinStack.length > 0) {
      clearPinStack();
    }
  }, []);

  const isReady = urlname && user;

  const userToUse = user ?? serversideUser;
  const avatarImg = userToUse?.avatar
    ? userToUse.avatar_url ||
      imageUrl(userToUse.avatar, { width: 180, rect: "sq" })
    : "https://cdn.dancf.com/fe-assets/img/c62d185173c48b4c73f90ea6b7acee33.png";

  return (
    <>
      {user && (
        <Head>
          <title>{user.username}的花瓣个人主页-花瓣网</title>
          <meta itemProp="image" content={avatarImg} />
          <meta
            name="keywords"
            content={`${user.username},设计素材,设计灵感,创意灵感`}
          />
          <meta
            name="description"
            content={`${user.username}的花瓣个人主页。花瓣网, 设计师寻找灵感的天堂！图片素材领导者，帮你采集、发现网络上你喜欢的事物。你可以用它收集灵感,保存有用的素材,计划旅行,晒晒自己想要的东西`}
          />
        </Head>
      )}
      {!isReady ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            paddingTop: "100px",
          }}
        >
          <HBLoading size={"30px"} />
        </div>
      ) : (
        <UserInfo02
          user={user}
          initBoards={serversideBoards}
          initPins={serversidePins}
          initMusePins={serversideMusePins}
          initLikesPins={serversideLikesPins}
          initLikesBoards={serversideLikesBoards}
          initLikesTopics={serversideLikesTopics}
          initTags={serversideTags}
          initTagPins={serversideTagPins}
          openTab={openTab}
          tagName={tagName}
          query={restQuery}
        />
      )}
    </>
  );
}
